import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { addDays, format, isBefore, parseISO, startOfDay } from "date-fns";
import { Header } from "@/components/layout/Header";
import { useAuth } from "@/hooks/useAuth";
import { useModals } from "@/hooks/useModals";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  FileSpreadsheet,
  Receipt,
  AlertCircle,
  Database,
  CreditCard,
  Loader2,
  HelpCircle,
  Bell,
  Building2,
  MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { InvoiceUploadZone } from "@/components/par/InvoiceUploadZone";
import { NotificationSettingsModal } from "@/components/par/NotificationSettingsModal";
import { InvoiceDetailTable } from "@/components/par/InvoiceDetailTable";
import { VendorDrillDown } from "@/components/par/VendorDrillDown";
import { OutstandingInvoiceTable } from "@/components/par/OutstandingInvoiceTable";
import { usePARReminders } from "@/hooks/usePARReminders";
import { supabase } from "@/integrations/supabase/client";
import { OptimizeTab } from "@/components/par/OptimizeTab";
import { PastPaymentRunView } from "@/components/par/PastPaymentRunView";
import { DataManagementPanel } from "@/components/par/DataManagementPanel";
import { NotificationsTab } from "@/components/par/NotificationsTab";
import { PaymentRunsTab } from "@/components/par/PaymentRunsTab";
import { usePaymentRuns } from "@/hooks/usePaymentRuns";
import type { PARInvoiceRow, VendorSummary } from "@/lib/par/csvInvoiceParser";
import { buildVendorSummaries } from "@/lib/par/csvInvoiceParser";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { ChatPanel } from "@/components/ChatPanel";
import { NegotiationsTab } from "@/components/negotiations/NegotiationsTab";

// ---------------------------------------------------------------------------
// Navigation config — single source of truth for two-level navigation.
// ---------------------------------------------------------------------------

/** All valid child tab values (matches the existing Tabs value= strings). */
export type ChildTabValue =
  | "outstanding"
  | "payment-runs"
  | "invoices"
  | "negotiations"
  | "data"
  | "notifications";

/** Top-level group config. */
interface TabGroup {
  id: string;
  label: string;
  children: ChildTabValue[];
}

export const TAB_GROUPS: TabGroup[] = [
  { id: "invoices-group", label: "Invoices", children: ["outstanding"] },
  { id: "payments-group", label: "Payments", children: ["payment-runs"] },
  {
    id: "vendors-group",
    label: "Vendors",
    children: ["invoices", "negotiations"],
  },
  { id: "settings-group", label: "Settings", children: ["notifications", "data"] },
];

/** Human-readable labels for each child tab. */
export const CHILD_LABELS: Record<ChildTabValue, string> = {
  outstanding: "Outstanding Invoices",
  "payment-runs": "Payment Runs",
  invoices: "Vendor Analysis",
  negotiations: "Vendor Negotiations",
  data: "Data Management",
  notifications: "Notifications",
};

/**
 * Derive the active group from the active child tab.
 * Returns undefined if no group contains that child (shouldn't happen with valid values).
 */
export function getActiveGroup(activeTab: ChildTabValue): TabGroup | undefined {
  return TAB_GROUPS.find((g) => g.children.includes(activeTab));
}

/**
 * Get the first selectable child for a group, filtering out flag-gated tabs.
 */
export function getDefaultChild(group: TabGroup, vendorNegotiatorEnabled: boolean): ChildTabValue {
  const available = group.children.filter((c) => c !== "negotiations" || vendorNegotiatorEnabled);
  return available[0] ?? group.children[0];
}

// ---------------------------------------------------------------------------
// Shared trigger class
// ---------------------------------------------------------------------------
const TRIGGER_CLASS =
  "rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=active]:shadow-none px-4 py-2.5 text-sm gap-2 text-muted-foreground data-[state=active]:text-foreground data-[state=active]:font-medium whitespace-nowrap";

// ---------------------------------------------------------------------------

interface UploadBatch {
  id: string;
  fileName: string;
  rowCount: number;
  uploadedAt: string;
  invoiceDateRange?: { min: string; max: string };
}

/** Fetch all PAR invoice rows using parallel pagination for speed. */
async function fetchAllInvoiceRows(supabase: any): Promise<any[]> {
  const COLS =
    "id, vendor_name, vendor_number, invoice_number, terms, invoiced_date, due_date, early_pay_due_date, paid_date, payment_status, amount_invoiced, amount_paid, discount_pct, potential_savings, days_paid_early, upload_batch_id, file_name, created_at";
  const PAGE = 1000;

  // 1. Get total count with a lightweight HEAD request
  const { count, error: countErr } = await supabase
    .from("par_invoices")
    .select("id", { count: "exact", head: true });

  if (countErr || !count) return [];

  // 2. Fire all pages in parallel
  const pages = Math.ceil(count / PAGE);
  const results = await Promise.all(
    Array.from({ length: pages }, (_, i) =>
      supabase
        .from("par_invoices")
        .select(COLS)
        .order("invoiced_date", { ascending: true })
        .range(i * PAGE, (i + 1) * PAGE - 1)
    )
  );

  return results.flatMap((r: any) => r.data ?? []);
}

export default function PARSystemsPage() {
  const { signOut, session } = useAuth();
  const modals = useModals();
  const { toast } = useToast();
  const {
    settings: reminderSettings,
    saving: reminderSaving,
    saveSettings: saveReminderSettings,
    persistInvoices,
  } = usePARReminders();

  const [allInvoiceRows, setAllInvoiceRows] = useState<PARInvoiceRow[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [allVendors, setAllVendors] = useState<VendorSummary[]>([]);
  const [batches, setBatches] = useState<UploadBatch[]>([]);

  // Refresh key: incrementing forces a re-fetch of invoice data from DB (used by chat panel)
  const [chatRefreshKey, setChatRefreshKey] = useState(0);
  const handleChatRefresh = useCallback(() => setChatRefreshKey((k) => k + 1), []);

  // Initialize tab, vendor, and run from URL params so state survives a page refresh
  const VALID_TABS: ChildTabValue[] = [
    "outstanding",
    "invoices",
    "data",
    "notifications",
    "payment-runs",
    "negotiations",
  ];
  const _initialParams = new URLSearchParams(window.location.search);
  const _initialTab = _initialParams.get("tab") ?? "outstanding";
  const [activeTab, setActiveTab] = useState<ChildTabValue>(
    VALID_TABS.includes(_initialTab as ChildTabValue)
      ? (_initialTab as ChildTabValue)
      : "outstanding"
  );
  const [selectedVendor, setSelectedVendor] = useState<string | null>(
    _initialParams.get("vendor") ? decodeURIComponent(_initialParams.get("vendor")!) : null
  );

  const [showNotifications, setShowNotifications] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  // Vendor Detail modal — opened when user clicks a vendor row in Vendor Analysis
  const [showVendorModal, setShowVendorModal] = useState(false);
  // activeRunId: null = list view, "new" = new run scheduler, uuid = edit existing run
  const [activeRunId, setActiveRunId] = useState<string | null>(_initialParams.get("run") ?? null);
  // activeRunIsPast: true when the selected run's week has fully elapsed
  const [activeRunIsPast, setActiveRunIsPast] = useState(false);

  const { getRun } = usePaymentRuns();

  // On mount: if activeRunId was pre-set from a URL deep-link, load the run and
  // determine whether it's a past run so we show the correct view immediately.
  const _initialRunId = _initialParams.get("run");
  useEffect(() => {
    if (!_initialRunId || _initialRunId === "new") return;
    getRun(_initialRunId).then((run) => {
      if (!run) return;
      const sunday = addDays(parseISO(run.pay_date + "T00:00:00"), 6);
      setActiveRunIsPast(isBefore(sunday, startOfDay(new Date())));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run only on mount

  // Sync active tab, selected vendor, and run back to the URL (replaceState so history isn't polluted)
  const isMounted = useRef(false);
  useEffect(() => {
    // Skip the very first render — the URL already has the correct values (we read from it above)
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    const params = new URLSearchParams(window.location.search);
    params.set("tab", activeTab);
    if (selectedVendor) {
      params.set("vendor", encodeURIComponent(selectedVendor));
    } else {
      params.delete("vendor");
    }
    if (activeRunId) {
      params.set("run", activeRunId);
    } else {
      params.delete("run");
    }
    // thread param is managed by NegotiationsTab internally; keep it if present
    window.history.replaceState({}, "", `?${params.toString()}`);
  }, [activeTab, selectedVendor, activeRunId]);

  // Load shared invoice data from DB once the session is available.
  // Gating on session?.user?.id means the fetch starts as soon as the session is
  // restored from localStorage (~1ms), running in parallel with the profile fetch
  // instead of waiting for authLoading to go false.
  useEffect(() => {
    if (!session?.user?.id) return; // wait for a valid session before querying
    const CACHE_KEY = `par_invoices_${session.user.id}`;
    const CACHE_VERSION = "v1";

    // Try to serve from cache immediately
    const cachedRaw = sessionStorage.getItem(CACHE_KEY);
    if (cachedRaw) {
      try {
        const { version, data } = JSON.parse(cachedRaw);
        if (version === CACHE_VERSION && Array.isArray(data) && data.length > 0) {
          // Hydrate state from cache immediately
          const cachedRows = data as PARInvoiceRow[];
          setAllInvoiceRows(cachedRows);
          setAllVendors(buildVendorSummaries(cachedRows));
          // Still revalidate in background — do NOT return early
        }
      } catch {
        sessionStorage.removeItem(CACHE_KEY);
      }
    }

    (async () => {
      setDataLoading(true);
      // Fetch all pages in parallel for 3-5x faster load vs sequential pagination
      const data = await fetchAllInvoiceRows(supabase);
      setDataLoading(false);
      if (data.length === 0) return;
      const rows: (PARInvoiceRow & { _upload_batch_id?: string })[] = data.map((row) => ({
        db_id: row.id,
        vendor_name: row.vendor_name,
        vendor_number: row.vendor_number ?? "",
        invoice_number: row.invoice_number ?? "",
        description: "",
        status: "",
        terms: row.terms ?? "",
        invoiced_date: row.invoiced_date ?? "",
        due_date: row.due_date ?? "",
        early_pay_due_date: row.early_pay_due_date ?? "",
        paid_date: row.paid_date ?? "",
        payment_status: row.payment_status ?? "",
        amount_invoiced: row.amount_invoiced ?? 0,
        amount_paid: row.amount_paid ?? 0,
        amount_discount: (row.amount_invoiced ?? 0) - (row.amount_paid ?? 0),
        discount_pct: row.discount_pct ?? 0,
        potential_savings: row.potential_savings ?? 0,
        days_paid_early: row.days_paid_early ?? 0,
        invoice_year: row.invoiced_date
          ? new Date(row.invoiced_date).getFullYear()
          : new Date().getFullYear(),
        _upload_batch_id: (row as any).upload_batch_id ?? undefined,
      }));
      setAllInvoiceRows(rows);
      setAllVendors(buildVendorSummaries(rows));

      // Save fresh data to cache for next page load
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ version: CACHE_VERSION, data: rows }));
      } catch {
        // sessionStorage may be full — ignore
      }

      // Derive batches from DB rows — use created_at of first row per batch as uploadedAt
      const batchMap = new Map<
        string,
        { fileName: string; count: number; createdAt: string; minDate: string; maxDate: string }
      >();
      for (const row of data) {
        const bid = (row as any).upload_batch_id ?? "unknown";
        const existing = batchMap.get(bid);
        const rowCreatedAt = (row as any).created_at ?? new Date().toISOString();
        const invoicedDate: string = (row as any).invoiced_date ?? "";
        if (!existing) {
          batchMap.set(bid, {
            fileName: (row as any).file_name ?? "Unknown file",
            count: 1,
            createdAt: rowCreatedAt,
            minDate: invoicedDate,
            maxDate: invoicedDate,
          });
        } else {
          existing.count++;
          // Keep the earliest created_at as the upload timestamp
          if (rowCreatedAt < existing.createdAt) existing.createdAt = rowCreatedAt;
          if (invoicedDate && (!existing.minDate || invoicedDate < existing.minDate))
            existing.minDate = invoicedDate;
          if (invoicedDate && invoicedDate > existing.maxDate) existing.maxDate = invoicedDate;
        }
      }
      const loadedBatches: UploadBatch[] = Array.from(batchMap.entries()).map(
        ([id, { fileName, count, createdAt, minDate, maxDate }]) => ({
          id,
          fileName,
          rowCount: count,
          uploadedAt: createdAt,
          invoiceDateRange: minDate && maxDate ? { min: minDate, max: maxDate } : undefined,
        })
      );
      setBatches(loadedBatches);
    })();
  }, [session?.user?.id, chatRefreshKey]);

  const invoiceRows = allInvoiceRows;
  const vendors = allVendors;

  // today string — memoized so it doesn't cause useMemo dependencies to fire every render.
  // It re-evaluates only when the component first mounts (stable across normal interactions).

  const today = useMemo(() => new Date().toISOString().split("T")[0], []);

  // Outstanding Invoices tab: unpaid invoices with a future (or no) due date
  const outstandingInvoices = useMemo(() => {
    return allInvoiceRows.filter((r) => {
      const isPaid = !!r.paid_date || r.payment_status.toLowerCase().includes("paid");
      if (isPaid || r.amount_invoiced <= r.amount_paid) return false;
      if (r.due_date && r.due_date < today) return false;
      return true;
    });
  }, [allInvoiceRows, today]);

  // Early Payment Scheduler: all unpaid invoices — the Scheduler's own
  // buildEligibleInvoices filters by early_pay_due_date, so invoices with a
  // past due_date but future early pay window are still valid opportunities.
  const unpaidInvoices = useMemo(() => {
    return allInvoiceRows.filter((r) => {
      const isPaid = !!r.paid_date || r.payment_status.toLowerCase().includes("paid");
      return !isPaid && r.amount_invoiced > r.amount_paid;
    });
  }, [allInvoiceRows]);

  const handleInvoicesParsed = (
    rows: PARInvoiceRow[],
    _vendorList: VendorSummary[],
    fileName: string
  ) => {
    if (rows.length === 0) return;
    const batchId = crypto.randomUUID();

    // Deduplicate by vendor_name + invoice_number against existing rows
    let dedupedRows: PARInvoiceRow[] = [];
    setAllInvoiceRows((prev) => {
      const existing = new Set(prev.map((r) => `${r.vendor_name}|${r.invoice_number}`));
      dedupedRows = rows.filter((r) => !existing.has(`${r.vendor_name}|${r.invoice_number}`));
      const merged = [...prev, ...dedupedRows];
      setAllVendors(buildVendorSummaries(merged));
      return merged;
    });

    // Optimistically add the batch to state
    const dates = rows
      .map((r) => r.invoiced_date)
      .filter(Boolean)
      .sort();
    setBatches((prev) => [
      ...prev,
      {
        id: batchId,
        fileName,
        rowCount: rows.length,
        uploadedAt: new Date().toISOString(),
        invoiceDateRange: dates.length
          ? { min: dates[0], max: dates[dates.length - 1] }
          : undefined,
      },
    ]);

    // Persist to database (fire-and-forget, uses same batchId)
    persistInvoices(rows, fileName, batchId);

    setShowUploadModal(false);
  };

  const removeDataset = async (batchId: string) => {
    await supabase.from("par_invoices").delete().eq("upload_batch_id", batchId);
    // Reload all rows from DB after delete (parallel pagination for speed)
    const reloadData = await fetchAllInvoiceRows(supabase);
    const data = reloadData.length > 0 ? reloadData : null;
    if (data) {
      const reloaded: PARInvoiceRow[] = data.map((row) => ({
        db_id: row.id,
        vendor_name: row.vendor_name,
        vendor_number: row.vendor_number ?? "",
        invoice_number: row.invoice_number ?? "",
        description: "",
        status: "",
        terms: row.terms ?? "",
        invoiced_date: row.invoiced_date ?? "",
        due_date: row.due_date ?? "",
        early_pay_due_date: row.early_pay_due_date ?? "",
        paid_date: row.paid_date ?? "",
        payment_status: row.payment_status ?? "",
        amount_invoiced: row.amount_invoiced ?? 0,
        amount_paid: row.amount_paid ?? 0,
        amount_discount: (row.amount_invoiced ?? 0) - (row.amount_paid ?? 0),
        discount_pct: row.discount_pct ?? 0,
        potential_savings: row.potential_savings ?? 0,
        days_paid_early: row.days_paid_early ?? 0,
        invoice_year: row.invoiced_date
          ? new Date(row.invoiced_date).getFullYear()
          : new Date().getFullYear(),
      }));
      setAllInvoiceRows(reloaded);
      setAllVendors(buildVendorSummaries(reloaded));
    } else {
      setAllInvoiceRows([]);
      setAllVendors([]);
    }
    setBatches((prev) => prev.filter((b) => b.id !== batchId));
  };

  const markInvoicesPaid = useCallback(
    async (dbIds: string[]) => {
      const today = new Date().toISOString().split("T")[0];

      // Snapshot the before-state of affected rows so we can restore on undo
      const prevRows = allInvoiceRows.filter((r) => r.db_id && dbIds.includes(r.db_id));

      const { error } = await supabase
        .from("par_invoices")
        .update({ payment_status: "Paid", paid_date: today } as any)
        .in("id", dbIds);
      if (error) {
        toast({
          title: "Failed to mark as paid",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      setAllInvoiceRows((prev) =>
        prev.map((r) =>
          r.db_id && dbIds.includes(r.db_id)
            ? { ...r, payment_status: "Paid", paid_date: today, amount_paid: r.amount_invoiced }
            : r
        )
      );

      toast({
        title: `${dbIds.length} invoice${dbIds.length !== 1 ? "s" : ""} marked as paid`,
        action: (
          <ToastAction
            altText="Undo"
            onClick={async () => {
              // Revert DB: clear payment_status and paid_date
              await supabase
                .from("par_invoices")
                .update({ payment_status: "", paid_date: null } as any)
                .in("id", dbIds);
              // Restore previous local state for affected rows
              setAllInvoiceRows((prev) =>
                prev.map((r) => {
                  if (!r.db_id || !dbIds.includes(r.db_id)) return r;
                  const original = prevRows.find((p) => p.db_id === r.db_id);
                  return original ?? r;
                })
              );
            }}
          >
            Undo
          </ToastAction>
        ),
      });
    },
    [toast, allInvoiceRows]
  );

  const handleVendorDrillDown = (vendor: string) => {
    setSelectedVendor(vendor);
    setShowVendorModal(true);
  };

  const { isEnabled, loading: flagsLoading } = useFeatureFlags();
  // Payment Runs tab is always enabled
  const paymentRunsEnabled = true;
  const chatbotEnabled = isEnabled("chatbot");
  // While flags are still loading, treat vendor_negotiator as enabled so that:
  //   (a) a deep-link to ?tab=negotiations doesn't get redirected to outstanding, and
  //   (b) the sub-tab row renders the "Vendor Negotiations" trigger immediately.
  // Once loading completes the true value takes over with no flash if the flag is on,
  // or a one-time hide if the flag is off (acceptable; avoids the worse UX of losing
  // the active tab on every load when the flag IS enabled).
  const vendorNegotiatorEnabled = flagsLoading ? true : isEnabled("vendor_negotiator");

  /**
   * Cross-nav: navigate to the Negotiations tab and pre-select the given vendor.
   * Only callable when the vendor_negotiator flag is enabled.
   */
  const handleNegotiateVendor = (vendor: string) => {
    if (!vendorNegotiatorEnabled) return;
    setActiveTab("negotiations");
    // The NegotiationsTab will use the vendor name to open the start dialog
    // via its own state; we surface it via a URL param so deep links work.
    const params = new URLSearchParams(window.location.search);
    params.set("tab", "negotiations");
    params.set("vendor", encodeURIComponent(vendor));
    window.history.replaceState({}, "", `?${params.toString()}`);
  };

  /** Cross-nav: Negotiations → Vendor Detail modal. */
  const handleViewVendorFromNegotiations = (vendorName: string) => {
    setSelectedVendor(vendorName);
    setShowVendorModal(true);
  };

  const hasInvoices = allInvoiceRows.length > 0;

  // Track chat panel collapsed state so we can adjust main content padding
  const [chatCollapsed, setChatCollapsed] = useState(false);

  // ---------------------------------------------------------------------------
  // Derive the active group from the active child tab (single source of truth).
  // All setActiveTab() calls (including deep-link inits and cross-nav) will
  // automatically land in the right group without any extra wiring.
  // ---------------------------------------------------------------------------
  const activeGroup = getActiveGroup(activeTab);

  // Sub-tabs for the active group, filtered by feature flags
  const activeGroupChildren = useMemo(() => {
    if (!activeGroup) return [];
    return activeGroup.children.filter((c) => c !== "negotiations" || vendorNegotiatorEnabled);
  }, [activeGroup, vendorNegotiatorEnabled]);

  return (
    <div className="min-h-screen bg-background">
      <Header
        onShowAbout={() => modals.setShowAboutModal(true)}
        onShowDocumentation={() => modals.setShowDocumentationModal(true)}
        onShowFeedback={() => modals.setShowFeedbackModal(true)}
        onShowACHHistory={() => modals.setShowACHHistoryModal(true)}
        onSignOut={signOut}
      />

      <main
        className={`w-full px-6 py-6 space-y-6 transition-[padding] duration-300 ease-in-out ${chatbotEnabled ? (chatCollapsed ? "pr-14" : "pr-[calc(25vw+1.5rem)]") : ""}`}
      >
        {/* Page header */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-display font-bold text-foreground tracking-tight">
              PAR Systems
            </h2>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-sm text-muted-foreground">
                Manage outstanding payments and early pay opportunities
              </p>
              {batches.length > 0 &&
                (() => {
                  const latest = batches.reduce((a, b) =>
                    new Date(a.uploadedAt) > new Date(b.uploadedAt) ? a : b
                  );
                  return (
                    <span className="text-xs text-muted-foreground/70 border border-border/60 rounded px-1.5 py-0.5 bg-muted/30 shrink-0">
                      Data as of {format(new Date(latest.uploadedAt), "MMM d, yyyy")}
                    </span>
                  );
                })()}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground gap-1.5 text-xs"
            onClick={() => setShowHelp(true)}
          >
            <HelpCircle className="w-3.5 h-3.5" />
            How to use
          </Button>
        </div>

        {/* How to use modal */}
        <Dialog open={showHelp} onOpenChange={setShowHelp}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>How to use PAR Systems</DialogTitle>
              <DialogDescription>
                A guide to the four tabs and their key features.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto pr-1">
              <div className="text-sm space-y-4">
                <div>
                  <h4 className="font-medium text-sm">Getting started</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 mt-1 list-disc list-inside">
                    <li>Upload your AP invoice export as a CSV from the Data Management tab</li>
                    <li>
                      DiscoRun will automatically detect early payment opportunities from your
                      invoice terms
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-sm">Outstanding Invoices</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 mt-1 list-disc list-inside">
                    <li>View all unpaid invoices sorted by their upcoming deadline</li>
                    <li>
                      Invoices with an early pay opportunity are highlighted — act before the early
                      pay window closes
                    </li>
                    <li>Use "Mark as Paid" to track which invoices have been settled</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-sm">Early Payment Scheduler</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 mt-1 list-disc list-inside">
                    <li>
                      See which invoices are eligible for early payment discounts within your
                      selected date range
                    </li>
                    <li>Set a minimum invoice amount to focus on high-value opportunities</li>
                    <li>Use "Calculate Savings" to see the total discount you could capture</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-sm">Vendor Analysis</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 mt-1 list-disc list-inside">
                    <li>
                      Understand payment patterns by vendor — average days early/late, discount
                      capture rate
                    </li>
                    <li>Expand any vendor row to see individual invoice details</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-sm">Data Management</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 mt-1 list-disc list-inside">
                    <li>Upload new invoice CSV exports here to refresh your data</li>
                    <li>Review past upload batches and remove stale data as needed</li>
                  </ul>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ------------------------------------------------------------------ */}
        {/* Two-level navigation                                                 */}
        {/* Row 1: top-level groups                                              */}
        {/* Row 2: sub-tabs for the active group (omitted for single-child groups) */}
        {/* Row 3: active sub-tab content (shadcn Tabs drives content only)     */}
        {/* ------------------------------------------------------------------ */}

        {/* Top-level group row */}
        <div
          className="border-b border-border"
          role="tablist"
          aria-label="Navigation groups"
          data-testid="group-tablist"
        >
          <div className="flex gap-0">
            {TAB_GROUPS.map((group) => {
              const isActive = activeGroup?.id === group.id;
              return (
                <button
                  key={group.id}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`group-panel-${group.id}`}
                  data-testid={`group-tab-${group.id}`}
                  className={`rounded-none border-b-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40"
                  }`}
                  onClick={() => {
                    const defaultChild = getDefaultChild(group, vendorNegotiatorEnabled);
                    setActiveTab(defaultChild);
                  }}
                >
                  {group.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Shadcn Tabs wraps everything below so TabsContent can still work by value */}
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as ChildTabValue)}
          className="space-y-4"
        >
          {/* Sub-tab row — only shown when the active group has more than one visible child */}
          {activeGroupChildren.length > 1 && (
            <TabsList
              className="h-auto p-0 bg-transparent border-b border-border rounded-none w-full justify-start gap-0"
              data-testid="subtab-tablist"
            >
              {activeGroupChildren.map((child) => {
                if (child === "payment-runs") {
                  return (
                    <TabsTrigger
                      key={child}
                      value={child}
                      className={TRIGGER_CLASS}
                      onClick={() => setActiveRunId(null)}
                    >
                      <CreditCard className="w-4 h-4 shrink-0" />
                      {CHILD_LABELS[child]}
                    </TabsTrigger>
                  );
                }
                const icon = {
                  outstanding: <AlertCircle className="w-4 h-4 shrink-0" />,
                  invoices: <Receipt className="w-4 h-4 shrink-0" />,
                  negotiations: <MessageSquare className="w-4 h-4 shrink-0" />,
                  data: <Database className="w-4 h-4 shrink-0" />,
                  notifications: <Bell className="w-4 h-4 shrink-0" />,
                }[child];
                return (
                  <TabsTrigger key={child} value={child} className={TRIGGER_CLASS}>
                    {icon}
                    {CHILD_LABELS[child]}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          )}

          {/* Outstanding tab */}
          <TabsContent value="outstanding" className="space-y-4">
            {dataLoading && (
              <div className="flex items-center gap-2 text-muted-foreground text-sm pb-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading invoice data...
              </div>
            )}
            {hasInvoices ? (
              <OutstandingInvoiceTable
                invoices={outstandingInvoices}
                onOpenNotifications={() => setShowNotifications(true)}
                onMarkPaid={markInvoicesPaid}
                loading={dataLoading}
              />
            ) : dataLoading ? (
              <OutstandingInvoiceTable
                invoices={[]}
                onOpenNotifications={() => setShowNotifications(true)}
                onMarkPaid={markInvoicesPaid}
                loading={dataLoading}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
                <FileSpreadsheet className="w-10 h-10 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">
                  No invoice data loaded yet.{" "}
                  <button
                    className="text-primary underline underline-offset-2"
                    onClick={() => setShowUploadModal(true)}
                  >
                    Upload data
                  </button>{" "}
                  to get started.
                </p>
              </div>
            )}
          </TabsContent>

          {/* Payment Runs tab — list view by default; past run detail or scheduler when a run is selected */}
          <TabsContent value="payment-runs">
            {activeRunId === null ? (
              <PaymentRunsTab
                allInvoices={unpaidInvoices}
                onNewRun={() => {
                  setActiveRunIsPast(false);
                  setActiveRunId("new");
                }}
                onViewRun={(run) => {
                  const sunday = addDays(parseISO(run.pay_date + "T00:00:00"), 6);
                  setActiveRunIsPast(isBefore(sunday, startOfDay(new Date())));
                  setActiveRunId(run.id);
                }}
              />
            ) : activeRunIsPast && activeRunId !== "new" ? (
              /* Past run: show three-status breakdown view, no Optimize */
              <PastPaymentRunView
                runId={activeRunId}
                allInvoices={allInvoiceRows}
                onBack={() => setActiveRunId(null)}
              />
            ) : (
              /* Current/future run or new run: show OptimizeTab */
              <>
                {dataLoading && (
                  <div className="flex items-center gap-2 text-muted-foreground text-sm pb-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading invoice data...
                  </div>
                )}
                {hasInvoices || dataLoading ? (
                  <OptimizeTab
                    invoices={unpaidInvoices}
                    loading={dataLoading}
                    paymentRunsEnabled={paymentRunsEnabled}
                    runId={activeRunId === "new" ? null : activeRunId}
                    onCancel={() => setActiveRunId(null)}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
                    <FileSpreadsheet className="w-10 h-10 text-muted-foreground/40" />
                    <p className="text-sm text-muted-foreground">
                      No invoice data loaded yet.{" "}
                      <button
                        className="text-primary underline underline-offset-2"
                        onClick={() => setShowUploadModal(true)}
                      >
                        Upload data
                      </button>{" "}
                      to get started.
                    </p>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Invoices tab */}
          <TabsContent value="invoices">
            {hasInvoices || dataLoading ? (
              <InvoiceDetailTable
                invoices={allInvoiceRows}
                onVendorDrillDown={handleVendorDrillDown}
                onNegotiate={vendorNegotiatorEnabled ? handleNegotiateVendor : undefined}
                loading={dataLoading}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
                <FileSpreadsheet className="w-10 h-10 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">
                  No invoice data loaded yet.{" "}
                  <button
                    className="text-primary underline underline-offset-2"
                    onClick={() => setShowUploadModal(true)}
                  >
                    Upload data
                  </button>{" "}
                  to get started.
                </p>
              </div>
            )}
          </TabsContent>

          {/* Data Management tab */}
          <TabsContent value="data">
            <DataManagementPanel
              batches={batches}
              onUpload={() => setShowUploadModal(true)}
              onDelete={removeDataset}
              loading={dataLoading}
            />
          </TabsContent>

          {/* Notifications tab */}
          <TabsContent value="notifications">
            <NotificationsTab
              settings={reminderSettings}
              saving={reminderSaving}
              onSave={saveReminderSettings}
            />
          </TabsContent>

          {/* Negotiations tab — flag-gated */}
          {vendorNegotiatorEnabled && (
            <TabsContent value="negotiations">
              <NegotiationsTab
                allInvoiceRows={allInvoiceRows}
                vendors={vendors}
                loading={dataLoading}
                onViewVendor={handleViewVendorFromNegotiations}
              />
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Vendor Detail modal — opened from Vendor Analysis row click */}
      <Dialog
        open={showVendorModal}
        onOpenChange={(open) => {
          setShowVendorModal(open);
          if (!open) setSelectedVendor(null);
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="px-6 pt-6 pb-0">
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              {selectedVendor ?? "Vendor Detail"}
            </DialogTitle>
          </DialogHeader>
          {selectedVendor && (
            <VendorDrillDown
              vendor={selectedVendor}
              invoices={allInvoiceRows}
              loading={dataLoading}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Upload New Data modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4" />
              Upload Invoice Data
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Upload your AP invoice details export (CSV or XLSX).
          </p>
          <InvoiceUploadZone onParsed={handleInvoicesParsed} />
          {hasInvoices && (
            <p className="text-[11px] text-muted-foreground">
              {vendors.length} vendor{vendors.length !== 1 ? "s" : ""} ·{" "}
              {invoiceRows.length.toLocaleString()} invoice rows currently loaded
            </p>
          )}
        </DialogContent>
      </Dialog>

      <NotificationSettingsModal
        open={showNotifications}
        onClose={() => setShowNotifications(false)}
        settings={reminderSettings}
        saving={reminderSaving}
        onSave={saveReminderSettings}
      />

      {chatbotEnabled && (
        <ChatPanel
          page="par-systems"
          invoices={allInvoiceRows}
          onCollapsedChange={setChatCollapsed}
          onRefresh={handleChatRefresh}
        />
      )}
    </div>
  );
}
